// Utilidades puras para relacionar activos con escenas, referencias y nodos.
// Se usan para (a) mostrar todas las versiones de una escena y (b) advertir
// antes de eliminar un archivo que todavía está referenciado en otro lugar.

/** IDs de todos los activos de una escena (lista explícita + activo seleccionado + activos con sceneId). */
export const sceneAssetIds = (scene, assets = []) => {
  if (!scene) return [];
  const ids = [...(scene.assetIds || [])];
  if (scene.selectedAssetId && !ids.includes(scene.selectedAssetId)) {
    ids.push(scene.selectedAssetId);
  }
  assets.forEach((a) => { if (a.sceneId === scene.id && !ids.includes(a.id)) ids.push(a.id); });
  return ids;
};

/** Activos (objetos) de una escena que existen en el estado, ordenados por fecha. */
export const sceneAssets = (scene, assets = []) => {
  const byId = Object.fromEntries(assets.map((a) => [a.id, a]));
  return sceneAssetIds(scene, assets)
    .map((id) => byId[id])
    .filter(Boolean)
    .sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
};

/**
 * Dónde se usan los activos indicados. No modifica nada.
 * @returns {{scenes: object[], references: object[], characters: object[], nodes: object[], derived: object[]}}
 */
export const findAssetUsage = (state, assetIds) => {
  const ids = new Set(assetIds || []);
  const scenes = state.scenes.filter((s) =>
    (s.assetIds || []).some((id) => ids.has(id)) || ids.has(s.selectedAssetId)
    || state.assets.some((a) => ids.has(a.id) && a.sceneId === s.id));
  const references = state.references.filter((r) => ids.has(r.assetId));
  const characters = state.characters.filter((c) => ids.has(c.masterAssetId) || (c.viewAssetIds || []).some((id) => ids.has(id)));
  const nodes = state.nodes.filter((n) => ids.has(n.data?.assetId));
  const derived = state.assets.filter((a) => !ids.has(a.id) && (ids.has(a.originalAssetId) || ids.has(a.parentAssetId)));
  return { scenes, references, characters, nodes, derived };
};

/**
 * Inspecciona minuciosamente cada recurso individual para identificar cuáles están protegidos
 * por estar en uso en escenas, referencias, personajes o nodos, y cuáles pueden eliminarse.
 *
 * @param {object} state Estado global de Studio
 * @param {string[]} assetIds IDs de recursos a evaluar
 * @param {string|null} contextSceneId Escena de contexto (si se evalúa "quitar de escena")
 * @returns {{
 *   protectedAssets: Array<{ id: string, asset: object, reasons: string[] }>,
 *   deletableAssetIds: string[],
 *   totalRequested: number,
 *   isAllProtected: boolean,
 *   isPartiallyProtected: boolean
 * }}
 */
export const inspectAssetsUsage = (state, assetIds = [], contextSceneId = null) => {
  const assetsById = Object.fromEntries((state.assets || []).map((a) => [a.id, a]));
  const scenes = state.scenes || [];
  const references = state.references || [];
  const characters = state.characters || [];
  const nodes = state.nodes || [];
  const projects = state.projects || [];

  const protectedAssets = [];
  const deletableAssetIds = [];

  for (const id of assetIds) {
    const asset = assetsById[id] || { id, label: "Recurso" };
    const reasons = [];

    // 1. Escenas que utilizan este recurso
    const usedScenes = scenes.filter((s) => {
      if (contextSceneId && s.id === contextSceneId) return false;
      return (s.assetIds || []).includes(id) || s.selectedAssetId === id || asset.sceneId === s.id;
    });
    for (const sc of usedScenes) {
      const proj = projects.find((p) => p.id === sc.projectId);
      const projLabel = proj ? ` · Proyecto «${proj.name}»` : "";
      reasons.push(`Escena «${sc.title || "Sin título"}»${projLabel}`);
    }

    // 2. Referencias del proyecto
    const usedRefs = references.filter((r) => r.assetId === id);
    for (const ref of usedRefs) {
      reasons.push(`Referencia «${ref.label || ref.id}»`);
    }

    // 3. Personajes (portada/master o vistas fijadas)
    const usedChars = characters.filter((c) => c.masterAssetId === id || (c.viewAssetIds || []).includes(id));
    for (const ch of usedChars) {
      reasons.push(`Personaje «${ch.name || ch.handle || "Sin nombre"}»`);
    }

    // 4. Nodos de lienzo
    const usedNodes = nodes.filter((n) => n.data?.assetId === id);
    if (usedNodes.length > 0) {
      reasons.push(`${usedNodes.length} nodo(s) del lienzo`);
    }

    if (reasons.length > 0) {
      protectedAssets.push({ id, asset, reasons });
    } else {
      deletableAssetIds.push(id);
    }
  }

  return {
    protectedAssets,
    deletableAssetIds,
    totalRequested: assetIds.length,
    isAllProtected: assetIds.length > 0 && protectedAssets.length === assetIds.length,
    isPartiallyProtected: protectedAssets.length > 0 && deletableAssetIds.length > 0,
  };
};
